import React, { useState, useEffect, useCallback } from 'react';
import { supermercadoStockService, productService, extractErrorDetails } from '../services/api';
import BarcodeScanner from './BarcodeScanner';
import ErrorDisplay from './ErrorDisplay';

function SupermercadoStockView({ supermercadoId = null }) {
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [editingMinStock, setEditingMinStock] = useState(false);
  const [minStockValue, setMinStockValue] = useState('');
  const [updatingMinStock, setUpdatingMinStock] = useState(false);
  const [success, setSuccess] = useState('');

  const fetchStock = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      
      let response;
      if (supermercadoId) {
        response = await supermercadoStockService.getStockBySupermercado(supermercadoId);
      } else {
        response = await supermercadoStockService.getMyStock();
      }
      
      if (response.success) {
        setStocks(response.data || []);
      } else {
        setError(response.message || 'Error al cargar el stock');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar el stock');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [supermercadoId]);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredStocks([]);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = stocks.filter(stock =>
        stock.productCodigoBarras.toLowerCase().includes(query) ||
        stock.productNombre.toLowerCase().includes(query)
      );
      setFilteredStocks(filtered.slice(0, 10));
    }
  }, [searchQuery, stocks]);

  const handleScan = async (barcode) => {
    setShowScanner(false);
    setSearchQuery(barcode);
    setError('');
    setErrorTrace(null);
    
    try {
      let response;
      if (supermercadoId) {
        response = await supermercadoStockService.getStockByBarcodeAndSupermercado(barcode, supermercadoId);
      } else {
        // For supermercado users, first get the product info
        const productResponse = await productService.getProductByBarcode(barcode);
        if (productResponse.success && productResponse.data) {
          // Then search in our stocks
          const found = stocks.find(s => s.productCodigoBarras === barcode);
          if (found) {
            setSelectedStock(found);
          } else {
            // Product exists but no stock in this supermercado
            setSelectedStock({
              productId: productResponse.data.id,
              productCodigoBarras: productResponse.data.codigoBarras,
              productNombre: productResponse.data.nombre,
              stock: 0
            });
          }
          setSearchQuery('');
          setFilteredStocks([]);
          return;
        } else {
          setError('Producto no encontrado con ese código de barras');
          return;
        }
      }
      
      if (response.success && response.data) {
        setSelectedStock(response.data);
        setSearchQuery('');
        setFilteredStocks([]);
      } else {
        setError(response.message || 'Producto no encontrado con ese código de barras');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Producto no encontrado con ese código de barras');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const selectStock = (stock) => {
    setSelectedStock(stock);
    setMinStockValue(stock.stockMinimo !== undefined && stock.stockMinimo !== null ? stock.stockMinimo.toString() : '0');
    setSearchQuery('');
    setFilteredStocks([]);
    setError('');
    setErrorTrace(null);
    setSuccess('');
    setEditingMinStock(false);
  };

  const clearSelection = () => {
    setSelectedStock(null);
    setError('');
    setErrorTrace(null);
    setSuccess('');
    setEditingMinStock(false);
  };

  const getMinStockWarningClass = (stock) => {
    return stock.stock <= (stock.stockMinimo || 0) && stock.stockMinimo > 0 ? 'warning' : '';
  };

  const handleEditMinStock = () => {
    setEditingMinStock(true);
    setError('');
    setSuccess('');
  };

  const handleCancelEditMinStock = () => {
    setEditingMinStock(false);
    setMinStockValue(selectedStock.stockMinimo !== undefined && selectedStock.stockMinimo !== null ? selectedStock.stockMinimo.toString() : '0');
    setError('');
  };

  const handleSaveMinStock = async () => {
    setError('');
    setErrorTrace(null);
    setSuccess('');

    if (!minStockValue.trim()) {
      setError('El stock mínimo es requerido');
      return;
    }

    const newMinStock = parseInt(minStockValue, 10);
    if (isNaN(newMinStock) || newMinStock < 0) {
      setError('El stock mínimo debe ser un número mayor o igual a 0');
      return;
    }

    try {
      setUpdatingMinStock(true);
      const effectiveSupermercadoId = supermercadoId || selectedStock.supermercadoId;
      
      const response = await supermercadoStockService.updateMinimumStock(
        effectiveSupermercadoId,
        selectedStock.productId,
        newMinStock
      );

      if (response.success) {
        setSuccess('Stock mínimo actualizado exitosamente');
        setEditingMinStock(false);
        
        // Update the selected stock
        const updatedStock = { ...selectedStock, stockMinimo: newMinStock };
        setSelectedStock(updatedStock);
        
        // Update in the stocks list
        setStocks(stocks.map(s => 
          s.productId === selectedStock.productId ? updatedStock : s
        ));
      } else {
        setError(response.message || 'Error al actualizar el stock mínimo');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al actualizar el stock mínimo');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setUpdatingMinStock(false);
    }
  };

  return (
    <div className="stock-management">
      <h2>Stock del Supermercado</h2>
      
      {showScanner && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      <div className="stock-layout">
        {/* Product Search Panel */}
        <div className="search-panel">
          <h3>Buscar Producto</h3>
          <div className="search-input-container">
            <div className="input-with-button">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código o nombre..."
                className="search-input"
              />
              <button 
                type="button" 
                onClick={() => setShowScanner(true)}
                className="btn btn-icon"
                title="Escanear código de barras"
              >
                📷
              </button>
            </div>
          </div>

          {loading ? (
            <p className="loading-text">Cargando stock...</p>
          ) : (
            <>
              {filteredStocks.length > 0 && (
                <div className="product-results">
                  <h4>Resultados ({filteredStocks.length})</h4>
                  <ul className="product-list">
                    {filteredStocks.map(stock => (
                      <li 
                        key={stock.id || stock.productId}
                        onClick={() => selectStock(stock)}
                        className="product-item"
                      >
                        <span className="product-code">{stock.productCodigoBarras}</span>
                        <span className="product-name">{stock.productNombre}</span>
                        <span className={`stock-info ${stock.stock > 0 ? 'positive' : stock.stock < 0 ? 'negative' : 'zero'}`}>
                          Stock: {stock.stock}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {searchQuery.length >= 2 && filteredStocks.length === 0 && (
                <p className="no-results">No se encontraron productos</p>
              )}

              {searchQuery.length === 0 && !selectedStock && (
                <p className="search-hint">Escriba al menos 2 caracteres o escanee un código de barras</p>
              )}
            </>
          )}
        </div>

        {/* Stock Display Panel */}
        <div className="adjustment-panel">
          <h3>Información de Stock</h3>
          
          {selectedStock ? (
            <div className="selected-product-card">
              <div className="product-info">
                <div className="product-header">
                  <h4>{selectedStock.productNombre}</h4>
                  <button 
                    type="button" 
                    onClick={clearSelection}
                    className="btn btn-small btn-secondary"
                  >
                    ✕ Cambiar
                  </button>
                </div>
                <p className="product-code">Código: {selectedStock.productCodigoBarras}</p>
                <p className="current-stock">
                  Stock en supermercado: 
                  <span className={`stock-value ${selectedStock.stock > 0 ? 'positive' : selectedStock.stock < 0 ? 'negative' : 'zero'}`}>
                    {selectedStock.stock}
                  </span>
                </p>
                
                {/* Minimum Stock Section */}
                <div className="minimum-stock-section">
                  <label>Stock Mínimo:</label>
                  {editingMinStock ? (
                    <div className="edit-min-stock">
                      <input
                        type="number"
                        min="0"
                        value={minStockValue}
                        onChange={(e) => setMinStockValue(e.target.value)}
                        className="min-stock-input"
                        disabled={updatingMinStock}
                      />
                      <button
                        type="button"
                        onClick={handleSaveMinStock}
                        className="btn btn-small btn-primary"
                        disabled={updatingMinStock}
                      >
                        {updatingMinStock ? 'Guardando...' : '✓ Guardar'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEditMinStock}
                        className="btn btn-small btn-secondary"
                        disabled={updatingMinStock}
                      >
                        ✕ Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="display-min-stock">
                      <span className="min-stock-value">
                        {selectedStock.stockMinimo !== undefined && selectedStock.stockMinimo !== null ? selectedStock.stockMinimo : 0}
                      </span>
                      <button
                        type="button"
                        onClick={handleEditMinStock}
                        className="btn btn-small btn-secondary"
                      >
                        ✏️ Editar
                      </button>
                    </div>
                  )}
                </div>
                
                {selectedStock.supermercadoNombre && (
                  <p className="supermercado-name">Supermercado: {selectedStock.supermercadoNombre}</p>
                )}
              </div>

              {success && <p className="success-message">{success}</p>}
              <ErrorDisplay error={error} errorTrace={errorTrace} />
            </div>
          ) : (
            <div className="no-selection">
              <p>Seleccione un producto para ver su stock</p>
            </div>
          )}
        </div>
      </div>

      {/* Stock Summary Table */}
      {!loading && stocks.length > 0 && !selectedStock && (
        <div className="stock-summary">
          <h3>Resumen de Stock</h3>
          <table className="stock-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th>Stock</th>
                <th>Stock Mín.</th>
              </tr>
            </thead>
            <tbody>
              {stocks.slice(0, 20).map(stock => (
                <tr key={stock.id || stock.productId} onClick={() => selectStock(stock)} className="clickable-row">
                  <td>{stock.productCodigoBarras}</td>
                  <td>{stock.productNombre}</td>
                  <td className={stock.stock > 0 ? 'positive' : stock.stock < 0 ? 'negative' : 'zero'}>
                    {stock.stock}
                  </td>
                  <td className={getMinStockWarningClass(stock)}>
                    {stock.stockMinimo !== undefined && stock.stockMinimo !== null ? stock.stockMinimo : 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {stocks.length > 20 && (
            <p className="more-items">Mostrando 20 de {stocks.length} productos. Use la búsqueda para encontrar productos específicos.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default SupermercadoStockView;
